import { useState, useEffect, useContext } from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Avatar,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';

const textFieldSx = {
  '& .MuiInputLabel-root': { color: 'var(--ink-3)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--ink-2)' },
  '& .MuiInputBase-input': { color: 'var(--ink)' },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'var(--ink-3)' },
  '& .MuiFormHelperText-root': { color: 'var(--ink-3)' },
  '& .MuiInput-underline:before': { borderBottomColor: 'var(--glass-border)' },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'var(--glass-border-strong)',
  },
  '& .MuiInput-underline:after': { borderBottomColor: '#f05a5a' },
};

const accordionSx = {
  mb: 1,
  borderRadius: '12px !important',
  border: '1px solid var(--glass-border)',
  background: 'transparent',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { mb: 1 },
};

const Profile = () => {
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [appNotifications, setAppNotifications] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const { token } = useContext(AuthContext);
  const { requestStatus, sendRequest, clearRequestStatus } = useAxios();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await sendRequest(
          `/users/profile`,
          'GET',
          null,
          { Authorization: `Bearer ${token}` },
          'Načítám profil...'
        );

        if (response.user) {
          setFirstName(response.user.firstName || '');
          setSurname(response.user.lastName || '');
          setEmail(response.user.email || '');
          setEmailNotifications(response.user.shiftEmailNotifications !== false);
          setAppNotifications(response.user.shiftAppNotifications !== false);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      loadProfile();
    }
  }, [token, sendRequest]);

  const handleSave = async () => {
    try {
      await sendRequest(
        `/users/profileMobile`,
        'PATCH',
        {
          firstName,
          lastName: surname,
          shiftEmailNotifications: emailNotifications,
          shiftAppNotifications: appNotifications,
        },
        { Authorization: `Bearer ${token}` },
        'Ukládám...'
      );
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ py: 1.5 }}>
        <Box className="glass-surface-accent glass-panel" sx={{ p: 2, borderRadius: 2, mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 52,
                height: 52,
                background: 'linear-gradient(135deg, #f05a5a 0%, #cf2e2e 100%)',
                fontWeight: 700,
              }}
            >
              {firstName?.[0] || ''}
              {surname?.[0] || ''}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ lineHeight: 1.15, color: 'var(--ink)', fontWeight: 700 }}
              >
                {firstName || 'Uživatel'} {surname || ''}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--ink-3)', mt: 0.25 }}>
                {email || 'Mobilní profil'}
              </Typography>
            </Box>
          </Stack>

          {isLoading && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, color: 'var(--ink-3)' }}
            >
              <CircularProgress size={18} sx={{ color: 'var(--ink-3)' }} />
              <Typography variant="body2">Načítám profil...</Typography>
            </Box>
          )}
        </Box>

        <Box className="glass-surface-accent glass-panel" sx={{ p: 0.5, borderRadius: 2, mb: 1.5 }}>
          <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: 'var(--ink-3)' }} />}
              sx={{ px: 1.25, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--ink)' }}>
                Osobní údaje
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.5 }}>
              <TextField
                fullWidth
                variant="standard"
                label="Jméno"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                sx={{ ...textFieldSx, mb: 2 }}
              />
              <TextField
                fullWidth
                variant="standard"
                label="Příjmení"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                disabled={isLoading}
                sx={{ ...textFieldSx, mb: 2 }}
              />
              <TextField
                fullWidth
                variant="standard"
                label="Email"
                value={email}
                disabled
                helperText="Email lze změnit pouze v hlavní aplikaci"
                sx={textFieldSx}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={{ ...accordionSx, mb: 0 }}>
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: 'var(--ink-3)' }} />}
              sx={{ px: 1.25, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--ink)' }}>
                Notifikace směn
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.5 }}>
              <Typography variant="body2" sx={{ color: 'var(--ink-3)', mb: 2 }}>
                Spravujte, jak chcete dostávat informace o nabídkách směn a změnách dostupnosti.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label="Emailové notifikace"
                sx={{ mb: 0.5, display: 'block', color: 'var(--ink-2)' }}
              />
              <Typography
                variant="caption"
                sx={{ ml: 4, mb: 2, display: 'block', color: 'var(--ink-3)' }}
              >
                Dostávat upozornění o nabídkách směn emailem
              </Typography>

              <Box sx={{ borderTop: '1px solid var(--glass-border)', my: 1.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={appNotifications}
                    onChange={(e) => setAppNotifications(e.target.checked)}
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label="Push notifikace v aplikaci"
                sx={{ mb: 0.5, display: 'block', color: 'var(--ink-2)' }}
              />
              <Typography variant="caption" sx={{ ml: 4, display: 'block', color: 'var(--ink-3)' }}>
                Dostávat okamžitá upozornění přímo v aplikaci
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Button
          fullWidth
          variant="contained"
          className="glass-accent-fill"
          onClick={handleSave}
          disabled={isLoading}
          sx={{ boxShadow: '0 4px 12px rgb(240 90 90 / 0.3)' }}
        >
          Uložit změny
        </Button>
      </Container>

      <Snackbar
        open={!!requestStatus}
        autoHideDuration={requestStatus?.duration || 6000}
        onClose={clearRequestStatus}
      >
        <Alert
          severity={requestStatus?.severity || 'info'}
          onClose={clearRequestStatus}
          sx={{
            backgroundColor: 'var(--glass-panel-veil)',
            color: 'var(--ink)',
            border: '1px solid var(--glass-border)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {requestStatus?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Profile;
